import { useMemo } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import * as api from 'api/entityApi'
import { CustomFields } from 'components/edit/CustomFields'
import { InlineEditField } from 'components/edit/InlineEditField'
import { MultiContactField } from 'components/edit/MultiContactField'
import { ProfileImageUploader } from 'components/edit/ProfileImageUploader'
import { TagInput } from 'components/edit/TagInput'
import { EntityActionWidget } from 'components/edit/EntityActionWidget'
import { ErrorBoundary } from 'components/common/ErrorBoundary'
import { ErrorState, PageLoader } from 'components/common/Loader'
import { useToast } from 'components/common/Toast'
import { useEntityEditForm } from 'hooks/useEntityEditForm'
import { usePermissions } from 'hooks/usePermissions'
import { resolveEntityParams } from 'utils/entityParams'
import type { EntityType, Option } from 'types/entity'

const SUPPORTED: EntityType[] = ['lead', 'enquiry']

/**
 * Replacement for EntityEdit.aspx + jsEntityEditObj.
 *
 * Each row keeps the inline pencil/save affordance (`SaveInlineData`) and the
 * footer performs the full-record save (`EditLead` / `EditEnquiry`). Supports encrypted query strings (?q=...).
 */
export default function EntityEdit() {
  const routeParams = useParams<{ entityType?: EntityType; entityId?: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const toast = useToast()

  // Route path, ?q=… or the legacy ?entityType=lead&entityID=<jsFlowCommon>&EditLead=…
  const { entityType, entityId, accessDenied, skipPermissionLookup } = useMemo(
    () => resolveEntityParams(location.search, routeParams),
    [location.search, routeParams],
  )

  const type = entityType as EntityType
  const supported = SUPPORTED.includes(type)
  const kind: 'lead' | 'enquiry' = type === 'enquiry' ? 'enquiry' : 'lead'

  // Role permissions per module (lead PAG10031 / enquiry PAG10002), same
  // source and semantics as the Sales module.
  const permissions = usePermissions(!skipPermissionLookup, api.MODULE_CODES[kind])

  const {
    form,
    setField,
    setMobile,
    setEmail,
    changeCountry,
    changeState,
    countries,
    states,
    cities,
    sources,
    mediums,
    campaigns,
    customFieldDefs,
    customFieldValues,
    setCustomField,
    dependencyMap,
    record,
    tableName,
    loading,
    saving,
    loadError,
    errors,
    save,
  } = useEntityEditForm(kind, entityId)

  // EntityEditDetails.js sends the user to /AccessDenied.aspx when entityID does
  // not decrypt to a positive id, rather than loading the form with no record.
  if (accessDenied) {
    return (
      <div className="app-shell">
        <ErrorState message="Access denied — this link's entityID could not be read." />
      </div>
    )
  }

  // Role gate: View === false on the module means no access at all (same as
  // the Sales module's /access-denied navigation).
  if (permissions.View === false) {
    return (
      <div className="app-shell">
        <ErrorState message="Access denied — your role does not include View permission for this module." />
      </div>
    )
  }

  if (!supported) {
    return (
      <div className="app-shell">
        <ErrorState message={`Entity type "${entityType}" has no edit screen. Supported: ${SUPPORTED.join(', ')}.`} />
      </div>
    )
  }

  if (loading) return <PageLoader />

  if (loadError) {
    return (
      <div className="app-shell">
        <ErrorState message={loadError} />
      </div>
    )
  }

  const handleSave = async () => {
    try {
      const result = await save()
      if (result === null) {
        toast.push('error', 'Please correct the highlighted errors.')
        return
      }
      toast.push('success', result || 'Saved successfully.')
    } catch (err) {
      toast.push('error', `Save failed: ${(err as Error).message}`)
    }
  }

  const inline = { entityId, tableName }

  return (
    <div className="app-shell">
      <header className="page-header">
        <div>
          <h1>{kind === 'lead' ? 'Edit Entity' : 'Edit Enquiry'}</h1>
          <div className="crumbs">
            {type} · {record && 'leadNo' in record ? `Lead No ${record.leadNo}` : entityId}
          </div>
        </div>
        <div className="page-actions">
          <Link className="btn" to={`/view/${type}/${entityId}`}>
            ← View
          </Link>
          {permissions.Edit && (
            <button type="button" className="btn pri-btn" disabled={saving} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </header>

      {errors.length > 0 && (
        <div className="alert alert-error">
          <strong>Please fix the following:</strong>
          <ul>
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSave()
        }}
      >
        <div className="edit-layout">
          <div>
            <section className="edit-section">
              <h5>Basic Details</h5>

              <ProfileImageUploader
                entityType={kind}
                entityId={entityId}
                disabled={!permissions.Edit}
              />

              <div className="edit-grid">
                <InlineEditField
                  label="Name"
                  required
                  fieldName="PersonName"
                  {...inline}
                  value={form.personName}
                  onChange={(v) => setField('personName', v)}
                >
                  {({ disabled }) => (
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Name"
                      autoComplete="off"
                      disabled={disabled}
                      value={form.personName}
                      onChange={(e) => setField('personName', e.target.value)}
                    />
                  )}
                </InlineEditField>

                <InlineEditField
                  label="Company"
                  fieldName="CompanyName"
                  {...inline}
                  value={form.companyName}
                  onChange={(v) => setField('companyName', v)}
                >
                  {({ disabled }) => (
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Company name"
                      autoComplete="off"
                      disabled={disabled}
                      value={form.companyName}
                      onChange={(e) => setField('companyName', e.target.value)}
                    />
                  )}
                </InlineEditField>

                <div>
                  <MultiContactField
                    label="Mobile"
                    fieldNames={['MobileNo', 'MobileNo1', 'MobileNo2']}
                    tableName={tableName}
                    entityId={entityId}
                    values={form.mobiles}
                    onChange={setMobile}
                    placeholder="+91 9999999999"
                  />
                </div>

                <div>
                  <MultiContactField
                    label="Email"
                    fieldNames={['EmailID', 'EmailID1', 'EmailID2']}
                    tableName={tableName}
                    entityId={entityId}
                    values={form.emails}
                    onChange={setEmail}
                    placeholder="name@company.com"
                  />
                </div>

                <SelectField
                  label="Country"
                  fieldName="Country"
                  {...inline}
                  options={countries}
                  value={form.countryId}
                  onSelect={(v) => void changeCountry(v)}
                  onInlineChange={(v) => void changeCountry(v)}
                />

                <SelectField
                  label="State"
                  fieldName="State"
                  {...inline}
                  options={states}
                  value={form.stateId}
                  onSelect={(v) => void changeState(v)}
                  onInlineChange={(v) => void changeState(v)}
                />

                <SelectField
                  label="City"
                  fieldName="City"
                  {...inline}
                  options={cities}
                  value={form.cityId}
                  onSelect={(v) => setField('cityId', v)}
                  onInlineChange={(v) => setField('cityId', v)}
                />

                <InlineEditField
                  label="Pincode"
                  fieldName="PinCode"
                  {...inline}
                  value={form.pinCode}
                  onChange={(v) => setField('pinCode', v)}
                >
                  {({ disabled }) => (
                    <input
                      type="text"
                      className="form-control"
                      inputMode="numeric"
                      disabled={disabled}
                      value={form.pinCode}
                      onChange={(e) => setField('pinCode', e.target.value)}
                    />
                  )}
                </InlineEditField>
              </div>
            </section>

            <section className="edit-section">
              <h5>Other Details</h5>

              <div className="edit-grid">
                <SelectField
                  label="Source"
                  required
                  fieldName="SourceName"
                  {...inline}
                  options={sources}
                  value={form.source}
                  placeholder="--select source--"
                  onSelect={(v) => setField('source', v)}
                  onInlineChange={(v) => setField('source', v)}
                />

                <SelectField
                  label="Medium"
                  fieldName="MediumName"
                  {...inline}
                  options={mediums}
                  value={form.medium}
                  placeholder="--select medium--"
                  onSelect={(v) => setField('medium', v)}
                  onInlineChange={(v) => setField('medium', v)}
                />

                <SelectField
                  label="Campaign"
                  fieldName="CampaignName"
                  {...inline}
                  options={campaigns}
                  value={form.campaign}
                  placeholder="--select campaign--"
                  onSelect={(v) => setField('campaign', v)}
                  onInlineChange={(v) => setField('campaign', v)}
                />

                <div className="span-2">
                  <InlineEditField
                    label="Residential Address"
                    fieldName="ResidentialAddress"
                    {...inline}
                    value={form.residentialAddress}
                    onChange={(v) => setField('residentialAddress', v)}
                  >
                    {({ disabled }) => (
                      <textarea
                        rows={2}
                        className="form-control"
                        placeholder="Address"
                        disabled={disabled}
                        value={form.residentialAddress}
                        onChange={(e) => setField('residentialAddress', e.target.value)}
                      />
                    )}
                  </InlineEditField>
                </div>

                <div className="span-2">
                  <InlineEditField
                    label="Official Address"
                    fieldName="OfficialAddress"
                    {...inline}
                    value={form.officialAddress}
                    onChange={(v) => setField('officialAddress', v)}
                  >
                    {({ disabled }) => (
                      <textarea
                        rows={2}
                        className="form-control"
                        placeholder="Address"
                        disabled={disabled}
                        value={form.officialAddress}
                        onChange={(e) => setField('officialAddress', e.target.value)}
                      />
                    )}
                  </InlineEditField>
                </div>

                <div className="span-2">
                  <InlineEditField
                    label={kind === 'lead' ? 'Initial Remarks' : 'Remarks'}
                    fieldName={kind === 'lead' ? 'InitialRemarks' : 'Remarks'}
                    {...inline}
                    value={form.remarks}
                    onChange={(v) => setField('remarks', v)}
                  >
                    {({ disabled }) => (
                      <textarea
                        rows={2}
                        className="form-control"
                        disabled={disabled}
                        value={form.remarks}
                        onChange={(e) => setField('remarks', e.target.value)}
                      />
                    )}
                  </InlineEditField>
                </div>
              </div>
            </section>

            <section className="edit-section">
              <h5>Custom Fields</h5>
              <CustomFields
                defs={customFieldDefs}
                values={customFieldValues}
                onChange={setCustomField}
                dependencyMap={dependencyMap}
              />
            </section>
          </div>

          <aside>
            <ErrorBoundary label="The action panel">
              <EntityActionWidget
                entityId={entityId}
                entityType={kind}
                permissions={permissions}
                contact={{
                  mobileNo: form.mobiles[0] ?? '',
                  mobileNo1: form.mobiles[1] ?? '',
                  mobileNo2: form.mobiles[2] ?? '',
                  email: form.emails[0] ?? '',
                  email1: form.emails[1] ?? '',
                  email2: form.emails[2] ?? '',
                }}
                onActionSuccess={(msg) => toast.push('success', msg)}
              />
            </ErrorBoundary>

            <section className="edit-section" style={{ marginTop: 16 }}>
              <h5>Tags</h5>
              <TagInput
                label={kind === 'lead' ? 'Lead tags' : 'Enquiry tags'}
                tags={form.tags}
                onChange={(tags) => setField('tags', tags)}
              />
            </section>

            <section className="edit-section">
              <h5>Summary</h5>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="k">Record:</span>
                  <span>{entityId}</span>
                </div>
                {record && 'leadNo' in record && (
                  <div className="detail-item">
                    <span className="k">Lead No:</span>
                    <span>{record.leadNo}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="k">Created On:</span>
                  <span>{record?.createdOn || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="k">Created By:</span>
                  <span>{record?.createdBy || '—'}</span>
                </div>
              </div>
            </section>
          </aside>
        </div>

        <div className="form-footer">
          <button type="button" className="btn" onClick={() => navigate(`/view/${type}/${entityId}`)}>
            Cancel
          </button>
          {permissions.Edit && (
            <button type="submit" className="btn pri-btn" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

/** A dropdown wrapped in the same inline-edit affordance as the text fields. */
function SelectField({
  label,
  required,
  fieldName,
  tableName,
  entityId,
  options,
  value,
  placeholder,
  onSelect,
  onInlineChange,
}: {
  label: string
  required?: boolean
  fieldName: string
  tableName: string
  entityId: string
  options: Option[]
  value: string
  placeholder?: string
  onSelect: (value: string) => void
  onInlineChange: (value: string) => void
}) {
  return (
    <InlineEditField
      label={label}
      required={required}
      fieldName={fieldName}
      tableName={tableName}
      entityId={entityId}
      value={value}
      onChange={onInlineChange}
    >
      {({ disabled }) => (
        <select
          className="form-control"
          disabled={disabled}
          value={value}
          onChange={(e) => onSelect(e.target.value)}
        >
          <option value="">{placeholder ?? '-- select --'}</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      )}
    </InlineEditField>
  )
}
